import type { ValidationRule } from './fieldValidators';
import {
  ConditionalValidators,
  FieldValidators,
  TypeValidators,
} from './fieldValidators';

// Helper to create message objects
const msg = (k: string) => ({ key: k });

// Centralized validation rules configuration
export const VALIDATION_RULES: Record<string, ValidationRule> = {
  // Personal Information
  'personalInfo.fullName': {
    required: true,
    message: msg('validation.personalInfo.fullName'),
    validator: v => TypeValidators.string(v),
  },
  'personalInfo.documentType': {
    required: true,
    message: msg('validation.personalInfo.documentType'),
    validator: v => TypeValidators.string(v),
  },
  'personalInfo.documentNumber': {
    required: true,
    message: msg('validation.personalInfo.documentNumber'),
    validator: v => TypeValidators.string(v),
  },
  'personalInfo.passportExpiry': {
    required: true,
    message: msg('validation.personalInfo.passportExpiry'),
    validator: v => TypeValidators.string(v),
  },
  'personalInfo.civilStatus': {
    required: true,
    message: msg('validation.personalInfo.civilStatus'),
    validator: v => TypeValidators.string(v),
  },
  'personalInfo.phone': {
    required: true,
    message: msg('validation.personalInfo.phone'),
    validator: v => TypeValidators.phone(v),
  },
  'personalInfo.email': {
    required: true,
    message: msg('validation.personalInfo.email'),
    validator: v => TypeValidators.email(v),
  },

  // Professional Information
  'professionalInfo.profession': {
    required: true,
    message: msg('validation.professionalInfo.profession'),
    validator: v => TypeValidators.string(v),
  },
  'professionalInfo.occupation': {
    required: true,
    message: msg('validation.professionalInfo.occupation'),
    validator: v => TypeValidators.string(v),
  },
  'professionalInfo.retiredCountry': {
    required: true,
    message: msg('validation.professionalInfo.retiredCountry'),
    validator: v => TypeValidators.string(v),
    conditional: ConditionalValidators.occupationIs('Jubilado/Pensionado'),
  },

  // FATCA Information - Conditional validation
  'fatcaInfo.usCitizen': {
    required: true,
    message: msg('validation.fatcaInfo.usCitizen'),
    validator: FieldValidators.fatcaYesNo,
    conditional: ConditionalValidators.fatcaField(''),
  },
  'fatcaInfo.hasGreenCard': {
    required: true,
    message: msg('validation.fatcaInfo.hasGreenCard'),
    validator: FieldValidators.fatcaYesNo,
    conditional: ConditionalValidators.fatcaField(''),
  },
  'fatcaInfo.stayed183': {
    required: true,
    message: msg('validation.fatcaInfo.stayed183'),
    validator: FieldValidators.fatcaYesNo,
    conditional: ConditionalValidators.fatcaField(''),
  },
  'fatcaInfo.tin': {
    required: true,
    message: msg('validation.fatcaInfo.tin'),
    validator: FieldValidators.fatcaTIN,
    conditional: ConditionalValidators.fatcaField(''),
  },
  'fatcaInfo.taxCountry': {
    required: true,
    message: msg('validation.fatcaInfo.taxCountry'),
    validator: FieldValidators.fatcaString,
    conditional: ConditionalValidators.fatcaField(''),
  },

  // Address Information
  'address.street': {
    required: true,
    message: msg('validation.address.street'),
    validator: v => TypeValidators.string(v),
  },
  'address.city': {
    required: true,
    message: msg('validation.address.city'),
    validator: v => TypeValidators.string(v),
  },
  'address.state': {
    required: true,
    message: msg('validation.address.state'),
    validator: v => TypeValidators.string(v),
  },
  'address.residence': {
    required: true,
    message: msg('validation.address.residence'),
    validator: v => TypeValidators.string(v),
  },

  // Extra Nationality Information - Conditional validation
  'address.extraNationality.nationalityCRS': {
    required: true,
    message: msg('validation.address.extraNationality.nationalityCRS'),
    validator: FieldValidators.extraNationalityString,
    conditional: ConditionalValidators.extraNationalityField(''),
  },
  'address.extraNationality.taxResidenceCountryCRS': {
    required: true,
    message: msg('validation.address.extraNationality.taxResidenceCountryCRS'),
    validator: FieldValidators.extraNationalityString,
    conditional: ConditionalValidators.extraNationalityField(''),
  },
  'address.extraNationality.hasTaxpayerNumber': {
    required: true,
    message: msg('validation.address.extraNationality.hasTaxpayerNumber'),
    validator: FieldValidators.extraNationalityYesNo,
    conditional: ConditionalValidators.extraNationalityField(''),
  },
  'address.extraNationality.taxpayerNumber': {
    required: true,
    message: msg('validation.address.extraNationality.taxpayerNumber'),
    validator: FieldValidators.taxpayerNumber,
    conditional: ConditionalValidators.taxpayerNumber(),
  },
  'address.extraNationality.taxpayerNumberReason': {
    required: true,
    message: msg('validation.address.extraNationality.taxpayerNumberReason'),
    validator: v => TypeValidators.string(v),
    conditional: ConditionalValidators.noTaxpayerField(),
  },
  'address.extraNationality.taxpayerNumberExplanation': {
    required: true,
    message: msg(
      'validation.address.extraNationality.taxpayerNumberExplanation',
    ),
    validator: FieldValidators.noTaxpayerExplanation,
    conditional: ConditionalValidators.noTaxpayerField(),
  },

  // Job Information
  'jobInfo.companyName': {
    required: true,
    message: msg('validation.jobInfo.companyName'),
    validator: v => TypeValidators.string(v),
  },
  'jobInfo.independentActivity': {
    required: true,
    message: msg('validation.jobInfo.independentActivity'),
    validator: v => TypeValidators.string(v),
    conditional: ConditionalValidators.occupationIs('Independiente'),
  },
  'jobInfo.initialDepositToOpenAccount': {
    required: true,
    message: msg('validation.jobInfo.initialDepositToOpenAccount'),
    validator: v => TypeValidators.currency(v),
  },

  // PEP Information - Conditional validation
  'jobInfo.pepInfo.pepInstitution': {
    required: true,
    message: msg('validation.jobInfo.pepInfo.pepInstitution'),
    validator: v => TypeValidators.string(v),
    conditional: ConditionalValidators.pepField(),
  },
  'jobInfo.pepInfo.pepPosition': {
    required: true,
    message: msg('validation.jobInfo.pepInfo.pepPosition'),
    validator: v => TypeValidators.string(v),
    conditional: ConditionalValidators.pepField(),
  },
  'jobInfo.pepInfo.pepPositionStartDate': {
    required: true,
    message: msg('validation.jobInfo.pepInfo.pepPositionStartDate'),
    validator: (v, user) => {
      if (!user.jobInfo?.pepInfo?.isPEP) return { isValid: true };
      return TypeValidators.string(v);
    },
    conditional: ConditionalValidators.pepField(),
  },
  'jobInfo.pepInfo.pepPositionEndDate': {
    required: true,
    message: msg('validation.jobInfo.pepInfo.pepPositionEndDate'),
    validator: FieldValidators.pepEndDateValidation,
    conditional: ConditionalValidators.pepField(),
  },

  // Family Member PEP Information - Conditional validation
  'jobInfo.pepInfo.familyMemberPepRelation': {
    required: true,
    message: msg('validation.jobInfo.pepInfo.familyMemberPepRelation'),
    validator: v => TypeValidators.string(v),
    conditional: ConditionalValidators.familyMemberPepField(),
  },
  'jobInfo.pepInfo.familyMemberPepPosition': {
    required: true,
    message: msg('validation.jobInfo.pepInfo.familyMemberPepPosition'),
    validator: v => TypeValidators.string(v),
    conditional: ConditionalValidators.familyMemberPepField(),
  },
  'jobInfo.pepInfo.familyMemberPepDate': {
    required: true,
    message: msg('validation.jobInfo.pepInfo.familyMemberPepDate'),
    validator: v => TypeValidators.string(v),
    conditional: ConditionalValidators.familyMemberPepField(),
  },

  // Finance Information
  'financeInfo.monthlyIncome': {
    required: true,
    message: msg('validation.financeInfo.monthlyIncome'),
    validator: v => TypeValidators.currency(v),
  },
  'financeInfo.variableIncome': {
    required: true,
    message: msg('validation.financeInfo.variableIncome'),
    validator: v => TypeValidators.boolean(v),
  },
  'financeInfo.countriesOfIncome': {
    required: true,
    message: msg('validation.financeInfo.countriesOfIncome'),
    validator: v => TypeValidators.array(v),
  },
  'financeInfo.deposits.amount': {
    required: true,
    message: msg('validation.financeInfo.deposits.amount'),
    validator: v => TypeValidators.currency(v),
  },
  'financeInfo.deposits.transactionsQuantity': {
    required: true,
    message: msg('validation.financeInfo.deposits.transactionsQuantity'),
    validator: v => TypeValidators.number(v),
  },
  'financeInfo.deposits.countriesOfOrigin': {
    required: true,
    message: msg('validation.financeInfo.deposits.countriesOfOrigin'),
    validator: v => TypeValidators.array(v),
  },
  'financeInfo.withdrawals.amount': {
    required: true,
    message: msg('validation.financeInfo.withdrawals.amount'),
    validator: v => TypeValidators.currency(v),
  },
  'financeInfo.withdrawals.transactionsQuantity': {
    required: true,
    message: msg('validation.financeInfo.withdrawals.transactionsQuantity'),
    validator: v => TypeValidators.number(v),
  },
  'financeInfo.withdrawals.countriesOfOrigin': {
    required: true,
    message: msg('validation.financeInfo.withdrawals.countriesOfOrigin'),
    validator: v => TypeValidators.array(v),
  },

  // Confirmation Information
  'confirmation.acceptedTerms': {
    required: true,
    message: msg('validation.confirmation.acceptedTerms'),
    validator: v => FieldValidators.mustBeTrue(v),
  },
};
